import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8011/api';

// Always forward cookies (useful if we later rely on cookie-based auth)
axios.defaults.withCredentials = true;

// React-router navigation bridge (set by App.tsx)
let navigationCallback: ((path: string) => void) | null = null;

export const setNavigationCallback = (callback: (path: string) => void) => {
  navigationCallback = callback;
};

type AuthClientOptions = {
  tokenKey: string;
  storageKeysToClear: string[];
  redirectPath: string;
};

const createAuthClient = ({ tokenKey, storageKeysToClear, redirectPath }: AuthClientOptions) => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        storageKeysToClear.forEach((key) => localStorage.removeItem(key));

        if (navigationCallback) {
          navigationCallback(redirectPath);
        } else {
          window.location.href = redirectPath;
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export const api = createAuthClient({
  tokenKey: 'token',
  storageKeysToClear: ['token', 'user'],
  redirectPath: '/login',
});

export const adminApi = createAuthClient({
  tokenKey: 'admin_token',
  storageKeysToClear: ['admin_token', 'admin_user'],
  redirectPath: '/admin',
});

export const apiKeyApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiKeyApi.interceptors.request.use(
  (config) => {
    const apiKey = localStorage.getItem('apiKey');
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
